import type {
  FundingColumn,
  FundingTableRow,
  HeatingFundingComponentRow,
  HeatingFundingPeriod,
  HeatingIncomeGroup,
  HeatingIncomeGroupCell,
  MaxEligibleExpensesRow,
} from '../types/funding-overview.types'

/**
 * Subsidy data for the Förderungen tab (BEG Einzelmaßnahmen).
 *
 * When subsidy rates change, update the values here — the layout is
 * rendered by the FundingOverview components.
 *
 * Source: Bundesamt für Wirtschaft und Ausfuhrkontrolle (BAFA)
 */

export const FUNDING_TITLE =
  'Förderübersicht: Bundesförderung für effiziente Gebäude – Einzelmaßnahmen'

export const ISFP_MEHRBETRAG_TOOLTIP =
  'Der iSFP-Bonus von +5 % gilt nur für förderfähige Ausgaben oberhalb des Standarddeckels.'

// ­ is a soft hyphen so long labels can wrap inside the narrow header cells
export const ENERGY_MEASURES_COLUMNS: FundingColumn[] = [
  { label: 'Zuschuss' },
  { label: 'iSFP-Bonus' },
  { label: 'WPB-Bonus' },
]

export const ENERGY_MEASURES_ROWS: FundingTableRow[] = [
  {
    measure: 'Gebäudehülle',
    measureSubtitle: 'Dämmung, Fenster, Außentüren, sommerlicher Wärmeschutz',
    cells: [
      '15 %',
      { text: '+5 % auf Mehrbetrag', pill: true, tooltip: ISFP_MEHRBETRAG_TOOLTIP },
      { text: '+5 % ab Q1/2027 nur Dämmung', pill: true },
    ],
  },
  {
    measure: 'Anlagentechnik',
    measureSubtitle: 'z. B. Lüftung, digitale Systeme',
    cells: [
      '15 %',
      { text: '+5 % auf Mehrbetrag', pill: true, tooltip: ISFP_MEHRBETRAG_TOOLTIP },
      null,
    ],
  },
  {
    measure: 'Heizungsoptimierung',
    measureSubtitle: 'zur Effizienzverbesserung',
    cells: [
      '15 %',
      { text: '+5 % auf Mehrbetrag', pill: true, tooltip: ISFP_MEHRBETRAG_TOOLTIP },
      null,
    ],
  },
  {
    measure: 'Heizungsoptimierung',
    measureSubtitle: 'zur Emissionsminderung',
    cells: ['50 %', null, null],
  },
]

export const HEATING_FUNDING = {
  title: 'Die künftige Heizungsförderung',
  subtitle:
    'Förderbausteine, Höchstsummen und -sätze je Einkommensgruppe — ab Inkrafttreten Ende 2026, dann im Halbjahrestakt',
  strapline:
    'Fördersatz = 30 % Grundförderung + Geschwindigkeitsbonus + Einkommensbonus · Deckel: 70 % (Einkommen bis 30.000 €: 80 %)',
  maxEligibleCostsLabel: 'Förderfähige Kosten höchstens (€)',
  componentsLabel: 'Die Fördersatz-Bausteine',
  eligibleMeasuresNote:
    'Förderfähige Heizungstechnik: Wärmepumpe, Solarthermie, Biomasse, Gebäude-/Wärmenetz, wasserstofffähige Heizungen, Brennstoffzellenheizung sowie innovative Heiztechnik auf Basis erneuerbarer Energien.',
  incomeGroupsLabel: 'Maximaler Zuschuss (in €) je Einkommensgruppe',
  incomeGroupsHint: 'Zu versteuerndes Haushaltseinkommen · Balken = Fördersatz in %',
  source:
    'Quelle: Regierungsentwurf für den Haushaltsausschuss (Bundesfinanzministerium), Stand 07/2026 — Entwurf, noch nicht beschlossen. Beträge = max. Zuschuss für die 1. Wohneinheit, selbstnutzende Eigentümer. Aufteilung Grundförderung/Geschwindigkeitsbonus ab Ende 2026 rechnerisch aus den BMF-Fördersätzen abgeleitet. Bis zum Inkrafttreten gelten die aktuellen Konditionen.',
}

export const HEATING_FUNDING_PERIODS: HeatingFundingPeriod[] = [
  { label: 'Ende 2026' },
  { label: 'I. 2027' },
  { label: 'II. 2027' },
  { label: 'I. 2028' },
  { label: 'II. 2028' },
]

export const HEATING_MAX_ELIGIBLE_COSTS = ['28.000', '27.250', '26.500', '25.750', '25.000']

export const HEATING_FUNDING_COMPONENTS: HeatingFundingComponentRow[] = [
  {
    name: 'Grundförderung',
    note: 'bleibt konstant',
    values: [30, 30, 30, 30, 30],
    tooltip: 'Bei Wärmepumpen ab I. 2027: 15 % Basis + 15 % nur bei Fertigung in der EU',
  },
  {
    name: 'Geschwindigkeitsbonus',
    note: 'sinkt um 4 Punkte je Halbjahr',
    values: [16, 12, 8, 4, null],
  },
]

export const HEATING_MAX_ELIGIBLE_EXPENSES = {
  title: 'Maximal förderfähige Ausgaben',
  subtitle:
    'Höchstgrenze der förderfähigen Ausgaben für Anlagen zur Wärmeerzeugung nach Nr. 5.3 – je Gebäude',
  columns: ['betroffene Wohneinheiten', 'Höchstgrenze'],
  rows: [
    { label: '1. Wohneinheit', amount: '28.000 €' },
    { label: '2. bis 6. Wohneinheit', amount: 'je +15.000 €' },
    { label: 'ab 7. Wohneinheit', amount: 'je +8.000 €' },
  ],
  hint: 'Die Grenze für die 1. Wohneinheit sinkt ab Ende 2026 im Halbjahrestakt – siehe „Förderfähige Kosten höchstens“ oben.',
}

export const HEATING_INCOME_GROUPS: HeatingIncomeGroup[] = [
  {
    range: '0 – 30.000',
    withChild: 'mit Kind 0 – 40.000',
    incomeBonus: '40 %',
    cells: [
      { amount: '22.400', rate: 80 },
      { amount: '21.800', rate: 80, delta: '-600' },
      { amount: '20.670', rate: 78, delta: '-1.730' },
      { amount: '19.055', rate: 74, delta: '-3.345' },
      { amount: '17.500', rate: 70, delta: '-4.900' },
    ],
  },
  {
    range: '30.001 – 40.000',
    withChild: 'mit Kind 40.001 – 50.000',
    incomeBonus: '30 %',
    cells: [
      { amount: '19.600', rate: 70 },
      { amount: '19.075', rate: 70, delta: '-525' },
      { amount: '18.020', rate: 68, delta: '-1.580' },
      { amount: '16.480', rate: 64, delta: '-3.120' },
      { amount: '15.000', rate: 60, delta: '-4.600' },
    ],
  },
  {
    range: '40.001 – 50.000',
    withChild: 'mit Kind 50.001 – 60.000',
    incomeBonus: '10 %',
    cells: [
      { amount: '15.680', rate: 56 },
      { amount: '14.170', rate: 52, delta: '-1.510' },
      { amount: '12.720', rate: 48, delta: '-2.960' },
      { amount: '11.330', rate: 44, delta: '-4.350' },
      { amount: '10.000', rate: 40, delta: '-5.680' },
    ],
  },
]

/** Grundförderung + Geschwindigkeitsbonus only — applies from 50.001 € income (no Einkommensbonus) */
export const HEATING_BASELINE_ROW = {
  name: 'Baseline Zuschuss (in €)',
  note: 'ohne Einkommensbonus – Einkommen ab 50.001 (mit Kind ab 60.001)',
  cells: [
    { amount: '12.880', rate: 46 },
    { amount: '11.445', rate: 42, delta: '-1.435' },
    { amount: '10.070', rate: 38, delta: '-2.810' },
    { amount: '8.755', rate: 34, delta: '-4.125' },
    { amount: '7.500', rate: 30, delta: '-5.380' },
  ] as HeatingIncomeGroupCell[],
}

export const MAX_ELIGIBLE_EXPENSES = {
  title: 'Maximal förderfähige Ausgaben',
  badge: 'Gemeinsamer Jahresrahmen',
  subtitle:
    'für Nr. 5.1 + 5.2 + 5.4 zusammen – je Gebäude und Kalenderjahr, unabhängig von der Zahl der Anträge',
  columns: ['betroffene Wohneinheiten', 'Standard', 'erhöhte Grenze¹'],
  footnote: '¹ bei gewährtem iSFP-Bonus oder fehlender iSFP-Antragsberechtigung nach EBW Nr. 5.2.',
  note: 'Hinw.: Bei Teilmaßnahmen zählen nur die tatsächlich betroffenen Wohneinheiten.',
  example: {
    title: 'iSFP richtig rechnen',
    finePrint: [
      'iSFP: +5 % nur oberhalb des Standarddeckels.',
      'Mind. 30.000 € · max. 15 Jahre · nicht für 5.4 b.',
    ],
    cases: [
      {
        label: '1 WE',
        subtitle: '60.000 € Kosten · iSFP-Bonus: ja',
        rows: [
          { label: 'Standard förderfähig:', value: '30.000 €' },
          { label: 'mit iSFP förderfähig:', value: '60.000 €' },
        ],
        lines: ['60.000 € × 15 %', '+ 30.000 € × 5 %'],
        result: '10.500 €',
      },
      {
        label: '2 WE',
        subtitle: '90.000 € Kosten · iSFP-Bonus: ja',
        rows: [
          { label: 'Standard förderfähig:', value: '45.000 €', calculation: '30.000 + 15.000' },
          { label: 'mit iSFP förderfähig:', value: '90.000 €', calculation: '60.000 + 30.000' },
        ],
        lines: ['90.000 € × 15 %', '+ 45.000 € × 5 %'],
        result: '15.750 €',
      },
      {
        label: '7 WE',
        subtitle: '225.000 € Kosten · iSFP-Bonus: ja',
        rows: [
          {
            label: 'Standard förderfähig:',
            value: '113.000 €',
            calculation: '30.000 + 5 × 15.000 + 8.000',
          },
          {
            label: 'mit iSFP förderfähig:',
            value: '225.000 €',
            calculation: '60.000 + 5 × 30.000 + 15.000',
          },
        ],
        lines: ['225.000 € × 15 %', '+ 112.000 € × 5 %'],
        result: '39.350 €',
      },
    ],
  },
}

export const MAX_ELIGIBLE_EXPENSES_ROWS: MaxEligibleExpensesRow[] = [
  { label: '1. Wohneinheit', standard: '30.000 €', increased: '60.000 €' },
  { label: '2. bis 6. Wohneinheit', standard: 'je +15.000 €', increased: 'je +30.000 €' },
  { label: 'ab 7. Wohneinheit', standard: 'je +8.000 €', increased: 'je +15.000 €' },
]

/**
 * Inputs for the interactive heat-pump example. Mirrors the future-funding
 * scheme above: Fördersatz = Grundförderung + Geschwindigkeitsbonus
 * + Einkommensbonus, gedeckelt, auf die förderfähigen Kosten der Periode.
 */
export const HEATPUMP_CALCULATOR = {
  title: 'Installation einer Wärmepumpe',
  subtitle: 'Beispielrechnung – Werte anpassbar',
  baseRate: 30,
  speedBonusByPeriod: [16, 12, 8, 4, 0],
  maxEligibleCostsByPeriod: [28000, 27250, 26500, 25750, 25000],
  additionalUnitCaps: { unit2to6: 15000, unitFrom7: 8000 },
  bonusHint:
    'Kosten gleichmäßig auf die Wohneinheiten verteilt; Geschwindigkeits- und Einkommensbonus gelten nur für die selbstgenutzte 1. Wohneinheit.',
  incomeTiers: [
    { label: '0 – 30.000', labelWithChild: '0 – 40.000', bonus: 40, capRate: 80 },
    { label: '30.001 – 40.000', labelWithChild: '40.001 – 50.000', bonus: 30, capRate: 70 },
    { label: '40.001 – 50.000', labelWithChild: '50.001 – 60.000', bonus: 10, capRate: 70 },
    { label: 'ab 50.001', labelWithChild: 'ab 60.001', bonus: 0, capRate: 70 },
  ],
  defaultCost: 25000,
}
